import React, { useEffect, useState } from "react";
import { Calendar, Smartphone, Download, ArrowRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { MONTHS_HE } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { uploadFileUrl, resolveProfileImage } from "@/lib/api";

const DEFAULT_PHOTO =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' fill='%23E5E7EB'/><circle cx='50' cy='38' r='18' fill='%2394A3B8'/><path d='M14 92c0-20 16-32 36-32s36 12 36 32' fill='%2394A3B8'/></svg>";

let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
  });
}

function InstallButton() {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem("install_dismissed") === "1"
  );
  const [available, setAvailable] = useState(!!deferredPrompt);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      deferredPrompt = e;
      setAvailable(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Detect standalone
  const isStandalone =
    (typeof window !== "undefined" &&
      window.matchMedia("(display-mode: standalone)").matches) ||
    (typeof window !== "undefined" && window.navigator.standalone);

  if (isStandalone || dismissed) return null;

  const isIos =
    typeof window !== "undefined" &&
    /iphone|ipad|ipod/i.test(window.navigator.userAgent) &&
    !window.navigator.userAgent.match(/CriOS|FxiOS/i);

  const doInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      deferredPrompt = null;
      setAvailable(false);
      if (choice.outcome === "accepted") {
        localStorage.setItem("install_dismissed", "1");
        setDismissed(true);
      }
    } else if (isIos) {
      setShowIosHint(true);
    } else {
      setShowIosHint(true);
    }
  };

  const dismiss = () => {
    localStorage.setItem("install_dismissed", "1");
    setDismissed(true);
  };

  return (
    <>
      <button
        onClick={doInstall}
        className="btn-primary text-sm"
        data-testid="install-app-btn"
        title="הורד כאפליקציה לנייד"
      >
        <Download size={14} /> הורד כאפליקציה
      </button>
      <button
        onClick={dismiss}
        className="btn-ghost text-xs"
        title="לא עכשיו"
        data-testid="install-dismiss-btn"
      >
        סגור
      </button>
      {showIosHint && (
        <div className="modal-backdrop" onClick={() => setShowIosHint(false)}>
          <div
            className="modal-panel p-6"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 420 }}
          >
            <h3 className="font-heebo font-bold text-xl mb-3">התקנה על הנייד</h3>
            <div className="space-y-2 text-slate-700">
              <p className="font-heebo">
                <span className="chip chip-info me-2">iOS · Safari</span>
                לחץ על <b>שתף</b> ובחר <b>&quot;הוסף למסך הבית&quot;</b>.
              </p>
              <p className="font-heebo">
                <span className="chip chip-purple me-2">Android · Chrome</span>
                תפריט (⋮) → <b>&quot;הוסף למסך הבית&quot;</b>.
              </p>
              <p className="text-xs text-slate-500 mt-3">
                אחרי ההתקנה האפליקציה תיפתח במסך מלא כמו אפליקציה רגילה.
              </p>
            </div>
            <button
              className="btn-primary w-full mt-4 justify-center"
              onClick={() => {
                setShowIosHint(false);
                dismiss();
              }}
            >
              הבנתי
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default function Header({
  month,
  year,
  view,
  setMonth,
  setYear,
  setView,
  showFilters = true,
  title,
  backTo,
  leftSlot,
}) {
  const years = [];
  const currY = new Date().getFullYear();
  for (let y = currY - 3; y <= currY + 2; y++) years.push(y);

  const location = useLocation();
  const isHome = location.pathname === "/app" || location.pathname === "/";
  const { user } = useAuth();
  const photo = user?.profile_image
    ? resolveProfileImage(user.profile_image)
    : (user?.profile_photo ? uploadFileUrl(user.profile_photo) : DEFAULT_PHOTO);
  const displayName = user?.full_name || user?.name || "";
  const gender = user?.gender || "male";
  const welcomeVerb = gender === "female" ? "ברוכה הבאה" : gender === "male" ? "ברוך הבא" : "ברוכים הבאים";

  return (
    <header
      className="border-b-2"
      style={{ background: "#FFFFFF", borderColor: "#C9C5B0" }}
      data-testid="app-header"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          {!isHome && backTo && (
            <Link
              to={backTo}
              className="btn-secondary"
              data-testid="header-back-btn"
              title="חזרה"
            >
              <ArrowRight size={16} /> חזרה
            </Link>
          )}
          <img
            src={photo}
            alt={displayName}
            className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg ring-2 ring-emerald-200"
            data-testid="header-avatar"
          />
          <div className="text-end">
            <div className="font-heebo font-black text-slate-900 text-2xl leading-tight">
              {displayName}
            </div>
            <div className="text-sm text-slate-500 mt-1 font-heebo">
              {title || "ניהול פיננסי אישי"}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {showFilters && (
            <>
              <div
                className="inline-flex bg-slate-100 rounded-2xl p-1 border border-slate-200"
                data-testid="view-toggle"
              >
                {[
                  { k: "month", label: "חודשי" },
                  { k: "year", label: "שנתי" },
                ].map((o) => (
                  <button
                    key={o.k}
                    onClick={() => setView(o.k)}
                    data-testid={`view-toggle-${o.k}`}
                    className={`px-4 py-1.5 rounded-xl text-sm font-heebo font-medium transition ${
                      view === o.k
                        ? "bg-white shadow text-slate-900"
                        : "text-slate-500"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>

              {view === "month" && (
                <div className="relative">
                  <Calendar
                    size={16}
                    className="absolute top-1/2 -translate-y-1/2 pointer-events-none"
                    style={{ insetInlineStart: "0.75rem" }}
                    color="#9CA3AF"
                  />
                  <select
                    value={month}
                    onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                    className="input-field ps-9"
                    data-testid="month-filter"
                    style={{ paddingInlineStart: "2.25rem" }}
                  >
                    {MONTHS_HE.map((m, i) => (
                      <option key={i} value={i + 1}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10))}
                className="input-field"
                data-testid="year-filter"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </>
          )}
          <InstallButton />
          {leftSlot}
        </div>
      </div>
    </header>
  );
}
